import React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Badge, Col, Row, Progress,
} from 'reactstrap';
import { renderToString } from 'react-dom/server';
import moment from 'moment';
import { Link } from '@s20/components/common/Link';
import i18n from '@s20/i18n';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths, MomentDateFormat } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    DateFilterDefaults,
    getComponents,
} from '../common/dataGrid/DataGrid';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageWrap, FlexEndRow, SmallButton, FlexColumnStart, FlexBetweenRow,
    Input,
} from '../common/forms/FormElements';
import authService from '../api-authorization/AuthorizeService';
import './MyTimesheets.scss';
import { TimesheetStatus } from './Timesheet';

const getStatusClassContext = (statusId) => {
    let classes = '';
    switch (statusId) {
        case TimesheetStatus.Submitted:
            classes = 'secondary';
            break;
        case TimesheetStatus.Approved:
            classes = 'success';
            break;
        case TimesheetStatus.Rejected:
            classes = 'danger';
            break;
        // DS/NF timesheets can be rejected by bill/payroll, but the emp
        // does not need to be informed of that status.
        case TimesheetStatus.BillingPayrollRejected:
            classes = 'success';
            break;
        case TimesheetStatus.OperationsReview:
            classes = 'secondary';
            break;
        default:
            break;
    }
    return classes;
};

// Map status id to a localized status label. The server returns
// timesheet.timesheetStatus as English text; the flagger should see it in
// their language.
const getStatusLabel = (statusId, fallback) => {
    switch (statusId) {
        case TimesheetStatus.New: return i18n.t('timesheet:statusNew');
        case TimesheetStatus.ClientReview: return i18n.t('timesheet:statusClientReview');
        case TimesheetStatus.OperationsReview: return i18n.t('timesheet:statusOperationsReview');
        case TimesheetStatus.Submitted: return i18n.t('timesheet:statusSubmitted');
        case TimesheetStatus.Rejected: return i18n.t('timesheet:statusRejected');
        case TimesheetStatus.CancelPending: return i18n.t('timesheet:statusCancelPending');
        case TimesheetStatus.ApprovalPending: return i18n.t('timesheet:statusApprovalPending');
        case TimesheetStatus.Approved: return i18n.t('timesheet:statusApproved');
        case TimesheetStatus.BillingPayrollRejected: return i18n.t('timesheet:statusApproved');
        case TimesheetStatus.Exported: return i18n.t('timesheet:statusExported');
        case TimesheetStatus.Deleted: return i18n.t('timesheet:statusDeleted');
        default: return fallback || '';
    }
};

// Overrides the default loading overlay.
const loadingOverlayTemplate = () => renderToString(
    <h4 className="d-flex flex-row align-items-center gridLoadingOverlay">
        <i className="fa fa-spin text-danger fa-lg fa-circle-notch mr-2" />
        {i18n.t('timesheet:gettingTimesheets')}
    </h4>,
);

export class MyTimesheetData {
    constructor(obj) {
        if (obj) {
            Object.assign(this, obj);
        }
    }

    billingStatus = null;

    billingStatusId = null;

    contractId = null;

    employeeFlatValue = null;

    employeeHoursValue = null;

    employeeHoursDuration = null;

    employeeId = null;

    employeeName = null;

    equipment = null;

    equipmentFlatValue = null;

    equipmentHoursValue = null;

    isSelfDispatching = null;

    jobDate = null;

    jobNumber = null;

    payrollStatus = null;

    payrollStatusId = null;

    timesheetId = null;

    timesheetNumber = null;

    timesheetStatus = null;

    timesheetStatusId = null;

    getFormattedDate = () => (this.jobDate ? moment(this.jobDate).format('dddd, M/D/YYYY') : null);
}

// For a mobile centric view, we render templated rows.
const fullWidthCellRenderer = (params) => {
    const data = params?.node?.data;
    if (data) {
        const timesheet = new MyTimesheetData(data);

        let url = `${AppNavPaths.Timesheet}/${timesheet.timesheetId}`;

        if (timesheet.isNonFlagging) {
            // Redirect to Non-Flagging Timesheet
            url = `${AppNavPaths.NonFlaggingTimesheet}/${timesheet.timesheetId}`;
        } else if (timesheet.isCrewLead) {
            // Redirect to General Timesheet if Crew Lead
            url = `${AppNavPaths.Timesheet}/${timesheet.timesheetId}`;
        } else if (timesheet.contractTimesheetResponsibilityTypeId === 2) {
            // Individual Responsibility
            url = `${AppNavPaths.Timesheet}/${timesheet.timesheetId}`;
        } else {
            // Redirect to a new page if not Crew Lead
            url = `${AppNavPaths.CrewTimesheet}/${timesheet.timesheetId}/${timesheet.employeeId}`;
        }

        return (
            <FlexColumnStart
                className="w-100 my-timesheet-row-content"
                id={`mytimesheet_${timesheet?.id}`}
                component={Link}
                to={url}
            >
                <FlexBetweenRow className="flex-fill w-100">
                    <span className="timesheet-date site-link cursor-pointer">
                        {timesheet.getFormattedDate()}
                    </span>
                    <FlexEndRow>
                        <span className="ml-2 timesheet-duration font-weight-bold">
                            {timesheet.employeeHoursDuration}
                        </span>
                    </FlexEndRow>
                </FlexBetweenRow>
                <FlexBetweenRow>
                    <span className="timesheet-number d-flex align-items-center">
                        {timesheet.timesheetNumber}
                        {timesheet.isNonFlagging && (
                            <Badge
                                color="info"
                                className="ml-2"
                            >{i18n.t('timesheet:nfBadge')}</Badge>
                        )}
                    </span>
                    <span className={`timesheet-status text-${getStatusClassContext(timesheet.timesheetStatusId)}`}>
                        {
                            timesheet.timesheetStatusId === TimesheetStatus.Rejected
                            && <span className="fa fa-exclamation-triangle mr-2" />
                        }
                        {getStatusLabel(timesheet.timesheetStatusId, timesheet.timesheetStatus)}
                    </span>
                </FlexBetweenRow>
            </FlexColumnStart>
        );
    }
    return null;
};

class MyTimesheets extends React.Component {
    static contextType = CommonContext;

    // #region SETUP
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            rowData: [],
            selectedMonth: moment().format('YYYY-MM'),
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        i18n.on('languageChanged', this.refreshGridLocale);
        this.populateState();
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
        i18n.off('languageChanged', this.refreshGridLocale);
    }

    // Re-applies translated grid strings when the user changes language at
    // runtime. Refreshes the column header in place; overlay templates are only
    // visible during loading/empty states and the next render picks up the new
    // values, so they don't need an explicit refresh here.
    refreshGridLocale = () => {
        const api = this.state.gridOptions?.api;
        if (!api) return;
        const [existingDef] = this.state.gridOptions.columnDefs;
        api.setColumnDefs([{
            ...existingDef,
            headerName: i18n.t('timesheet:timesheets'),
        }]);
        api.refreshHeader();
    };

    onAddNonFlagTimesheet = () => this.context.history.push(AppNavPaths.NonFlaggingTimesheetNew);

    onAddSelfDispatchTimesheet = () => this.context.history.push(AppNavPaths.SelfDispatchTimesheetNew);

    onMonthSelectionChanged = (event) => {
        const newMonth = event.target.value;
        this.setState({ selectedMonth: newMonth }, () => this.getTimesheets());
    };

    getTimesheets = () => {
        const dateFilterComponent = this.state.gridOptions.api.getFilterInstance('Date');
        this.setDateRange(dateFilterComponent);
        this.state.gridOptions.api.onFilterChanged();
    };

    // Sets the date filter from the dropdown selection, overriding the built-in filters.
    setDateRange = (dateRangeFilterComponent) => {
        const { selectedMonth } = { ...this.state };
        const mSelectedMonth = moment(selectedMonth, 'YYYY-MM');
        // Sets the range to be the range of the month selection.
        const startOfMonth = mSelectedMonth.startOf('month').format(MomentDateFormat.DateOnlyUTCHyphenated);
        const endOfMonth = mSelectedMonth.endOf('month').format(MomentDateFormat.DateOnlyUTCHyphenated);

        dateRangeFilterComponent.setModel({
            filterType: 'date',
            type: 'inRange',
            dateFrom: startOfMonth,
            dateTo: endOfMonth,
        });
    };

    setupGrid = () => {
        const context = this;

        const gridOptions = {
            ...createGridOptions(this),
            components: {
                ...getComponents(),
            },
            columnDefs: [{
                colId: 'Date',
                headerName: i18n.t('timesheet:timesheets'),
                field: 'date',
                sortable: false,
                filter: 'agDateColumnFilter',
                filterParams: {
                    ...DateFilterDefaults,
                    inRangeInclusive: true,
                    closeOnApply: true,
                    browserDatePicker: true,
                    suppressFilterButton: true,
                },
                floatingFilter: false,
                sort: { direction: 'desc', priority: 0 },
                cellRenderer: fullWidthCellRenderer,
                suppressMenu: true,
            }],
            customParametersFunction: () => ({ employeeId: this.context.user.employeeId }),
            getRowClass: (params) => {
                if (params?.node?.data?.timesheetStatusId) {
                    return [
                        `timesheet-status-${params.node.data.timesheetStatus.toLowerCase()}`,
                    ];
                }
            },
        };

        // Overrides of the default grid options
        gridOptions.rowHeight = 60;
        gridOptions.defaultColDef.floatingFilter = false; // Hide the floating filter bar.
        gridOptions.overlayLoadingTemplate = loadingOverlayTemplate(); // Apply our manual loading overlay.
        gridOptions.overlayNoRowsTemplate = i18n.t('timesheet:noTimesheetsFound');
        gridOptions.showLoadingOverlay = true; // enable overlays.

        // On the grid's first load, we want to apply the filters immediately from the state population,
        // before the API call is made.
        gridOptions.onGridReady = (params) => {
            const dateRangeFilterComponent = params.api.getFilterInstance('Date');
            this.setDateRange(dateRangeFilterComponent);
            params.api.onFilterChanged();
            gridOptions.api = params.api;
            params.api.setDatasource(context.state.dataSource, gridOptions);
        };

        // Apply the created server datasource to the grid.
        const dataSource = createDataSource(ApiRoutes.timesheet.byEmployeeSearch(), gridOptions);
        return { gridOptions, dataSource };
    };

    // #endregion

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const { user, tenant } = this.context;

            // Check tenant settings and user permissions for access to either self-dispatch ts or
            // non-flag ts.
            const access = await httpClient.js(ApiRoutes.contract.userHasSelfDispatching());

            const selfDispatchTimesheetCount = (await httpClient.js(ApiRoutes.selfDispatchTimesheet.count())) ?? 0;

            const hasAccess =
                // Does the user have any preentered timesheets?
                Boolean(selfDispatchTimesheetCount > 0)
                ||
                // Are there any self dispatching contracts for the user's dispatching center?
                // Does the user have the permission?
                Boolean(access.hasSelfDispatchingAccess && user.canCreateSelfDispatchTimesheet)
                ||
                // Are there any non-flagging contracts for the user's dispatching center?
                // Does the user have the permission?
                Boolean(access.hasNonFlaggingAccess && user.canCreateNonFlagTimesheet);

            if (!tenant.tenantSettings.enableSelfDispatchTimesheets || !hasAccess) {
                this.context.history.push(AppNavPaths.NotFound);
                return false;
            }

            const gridSetup = await this.setupGrid();

            // Save our grid options and dataSource to the state, so we can utilize it.
            this.setState({
                loading: false,
                gridOptions: gridSetup.gridOptions,
                dataSource: gridSetup.dataSource,
                canCreateSelfDispatchTimesheet: Boolean(user.canCreateSelfDispatchTimesheet),
                canCreateNonFlagTimesheet: Boolean(user.canCreateNonFlagTimesheet),
                hasSelfDispatchingAccess: access.hasSelfDispatchingAccess,
                hasNonFlaggingAccess: access.hasNonFlaggingAccess,
            });
        }
    };

    render() {
        if (this.state.loading) return (<Progress />);

        const {
            rowData, gridOptions, dataSource, canCreateNonFlagTimesheet,
            canCreateSelfDispatchTimesheet, hasSelfDispatchingAccess, hasNonFlaggingAccess,
        } = { ...this.state };
        const { t } = this.props;

        return (
            <PageWrap id="myTimesheetsContent">
                <Row className="flex-fill d-flex flex-column flex-nowrap">
                    <Col
                        xl="6"
                        lg="8"
                        md="10"
                        sm="12"
                        className="ml-auto mr-auto pt-2 flex-fill d-flex flex-column flex-nowrap"
                    >
                        <DataGridToolbar
                            entity="timesheet"
                            gridApi={gridOptions.api}
                            dataSource={dataSource}
                            hideAdd
                            hideExcelButton
                            hideClearFilterButton
                            gridOptions={gridOptions}
                        >
                            {
                                // TODO: Check for NF timesheet or SD timesheet perm, display proper button.
                                <FlexEndRow>
                                    <Input
                                        type="month"
                                        id="myTimesheetsMonthSelector"
                                        className="form-control-sm"
                                        value={this.state.selectedMonth ?? ''}
                                        onChange={this.onMonthSelectionChanged}
                                        formGroupClassName="mb-0"
                                    />
                                    {
                                        !!hasNonFlaggingAccess && !!canCreateNonFlagTimesheet
                                        && (
                                            <SmallButton
                                                disabled={!!this.state.saving}
                                                onClick={this.onAddNonFlagTimesheet}
                                                className="mr-0"
                                            >
                                                <i className="fa fa-plus" />
                                                <span className="small-viewport-hide ml-2">{t('timesheet:newNonFlaggingTimesheet')}</span>
                                            </SmallButton>
                                        )
                                    }
                                    {
                                        !!hasSelfDispatchingAccess && !!canCreateSelfDispatchTimesheet
                                        && (
                                            <SmallButton
                                                disabled={!!this.state.saving}
                                                onClick={this.onAddSelfDispatchTimesheet}
                                            >
                                                <i className="fa fa-plus" />
                                                <span className="small-viewport-hide ml-2">{t('timesheet:newSelfDispatchTimesheet')}</span>
                                            </SmallButton>
                                        )
                                    }
                                </FlexEndRow>
                            }
                        </DataGridToolbar>
                        <div className="p-1" />
                        <DataGrid
                            domLayout="normal"
                            rowData={rowData}
                            gridOptions={gridOptions}
                            gridStatus={this.state.gridStatus}
                        />
                    </Col>
                </Row>
            </PageWrap>
        );
    }
}

export default withTranslation()(MyTimesheets);
